import { DenormalizedCustomizableQuestion } from '@packages/types'
import React from 'react'

import * as customizationUtils from 'customizer/customization/utils'
import useThemeSettings from 'customizer/theme/useThemeSettings'

import ThumbnailAnswer from '../answer/ThumbnailAnswer'

import './ThumbnailQuestion.scss'

export interface ThumbnailQuestionProps {
  className?: string
  question: DenormalizedCustomizableQuestion
  pricing: { [answerId: string]: number | string }
  currency: string
  lang?: string
  country?: string
  requiredWarning?: boolean
  isLarge?: boolean
  selectAnswer: (answerId: string) => void
  focusQuestion: () => void
  themeConfig: { showLabel: boolean }
  answersOutOfStock: string[]
}

const ThumbnailQuestion = ({
  question,
  pricing,
  currency,
  lang,
  country,
  focusQuestion,
  selectAnswer,
  themeConfig,
  isLarge = false,
  answersOutOfStock,
}: ThumbnailQuestionProps) => {
  const hasNameLabel = themeConfig?.showLabel
  const thumbnailStyle: {
    thumbnailColumns: number
    thumbnailWidth: string
    thumbnailHeight: string
    thumbnailVerticalMargin: string
    thumbnailBorderRadius: string
    showMultiAnswerFeedback: boolean
    feedbackIconColor: string
    feedbackBackgroundColor: string
    feedbackBorderWidth: string
    feedbackBorderColor: string
    removeFeedbackIconColor: string
    removeFeedbackBackgroundColor: string
  } = useThemeSettings(isLarge ? 'largeThumbnailsButton' : 'thumbnailsButton')

  const { selectedAnswer, selectedAnswers, isMultiAnswer } = question
  const { thumbnailColumns, thumbnailWidth, thumbnailVerticalMargin } = thumbnailStyle

  const handleAnswerClick = (answerId: string) => {
    selectAnswer(answerId)
    focusQuestion()
  }

  const isDisabled = !!isMultiAnswer && customizationUtils.countSelectionsLeft(question) === 0

  return (
    <div className={`thumbnail-question thumbnail-question--${question.type}`} data-testid="thumbnail-question">
      <div
        className="answers"
        style={{
          gridTemplateColumns: `repeat(${thumbnailColumns}, min(${thumbnailWidth}, ${100 / thumbnailColumns}%))`,
          rowGap: thumbnailVerticalMargin,
        }}
      >
        {question.answers.map(answer => {
          const isSelected = isMultiAnswer
            ? !!selectedAnswers?.find(({ id }) => id === answer.id)
            : answer.id === selectedAnswer?.id

          const isOutOfStock = answersOutOfStock.includes(answer.id)

          return (
            <ThumbnailAnswer
              isLarge={isLarge}
              key={answer.id}
              {...answer}
              isMultiAnswer={question.isMultiAnswer}
              selected={isSelected}
              price={pricing?.[answer.id]}
              currency={currency}
              lang={lang}
              country={country}
              inputType={question.inputType}
              handleClick={handleAnswerClick}
              showLabel={hasNameLabel}
              thumbnailStyle={thumbnailStyle}
              disabled={isDisabled && !isSelected}
              isOutOfStock={isOutOfStock}
              showRelativePrice={question.showRelativePrice}
            />
          )
        })}
      </div>
    </div>
  )
}

export default ThumbnailQuestion
